import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  IconButton,
  Collapse,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SystemAccessLogStatusPill from './SystemAccessLogStatusPill';
import SystemAccessLogDetail from './SystemAccessLogDetail';
import { formatDate, formatScheduledDateBerlin, getLastFailedAttempt } from '../utils/format';
import type { ActionType, ResendMailSnapshot, SystemAccessLogEntry } from '../types';

const ACTION_CONFIG: Record<ActionType, { label: string; color: string }> = {
  apply: { label: 'Beantragen', color: '#4ADE80' },
  cancel: { label: 'Kündigen', color: '#F85149' },
  change: { label: 'Änderung', color: '#58A6FF' },
};

const ROWS_PER_PAGE_OPTIONS: Array<number | { label: string; value: number }> = [
  5,
  10,
  25,
  50,
  { label: 'Alle', value: -1 },
];

function getRelevantDate(entry: SystemAccessLogEntry): string {
  switch (entry.process_status) {
    case 'applied':
      return entry.executed_at || entry.createdAt;
    case 'planned':
      return entry.scheduled_at || entry.createdAt;
    case 'not_sent': {
      // `executed_at` is intentionally NULL on failure (see getLastFailedAttempt).
      // Use the latest failed-attempt timestamp so the date column shows the
      // failure day, not the creation day. Fall back to executed_at for legacy
      // rows from before the claim-before-send change, then createdAt.
      const lastFail = getLastFailedAttempt(entry.history);
      return lastFail ? lastFail.createdAt : entry.executed_at || entry.createdAt;
    }
    case 'cancelled':
      return entry.cancelled_at || entry.createdAt;
    default:
      return entry.createdAt;
  }
}

// `scheduled_at` is a date-only field anchored at 00:00 Europe/Berlin, so it
// must be rendered in Berlin TZ everywhere — otherwise the collapsed row and
// the expanded detail can show different calendar days in non-Berlin browsers.
// Event timestamps (executed_at/cancelled_at/createdAt) stay in local time.
function formatRowDate(entry: SystemAccessLogEntry): string {
  if (entry.process_status === 'planned' && entry.scheduled_at) {
    return formatScheduledDateBerlin(entry.scheduled_at);
  }
  return formatDate(getRelevantDate(entry));
}

interface Props {
  entries: SystemAccessLogEntry[];
  onCancel: (entry: SystemAccessLogEntry) => void;
  // Opens the edit dialog for a still-scheduled row (RITA-1317). Threaded down to the
  // detail row, mirroring onCancel.
  onEdit: (entry: SystemAccessLogEntry) => void;
  // Ids of entries whose saved edit is still awaiting its post-edit log refresh. Owned by the
  // panel so the row stays locked across collapse/paging/filtering; the detail uses it to keep
  // the edit/cancel affordances disabled until the refreshed (new) content lands (Audit P2).
  editPendingIds: ReadonlySet<number>;
  // Ids of entries whose confirmed resend is still awaiting a durable log refresh.
  // Owned by the panel so the latch survives a row collapsing/paging/filtering (which
  // unmount the detail under `Collapse unmountOnExit`) — see SystemAccessLogDetail's
  // resend latch (Codex Review P1).
  resentPendingIds: ReadonlySet<number>;
  // Marks the entry as resent in the panel and resolves once the log refetch settles;
  // rejects if it fails so the detail keeps the button replaced. The advisorId is the resent row's
  // own advisor_id so the panel can file the latch under the ORIGINATING advisor even if the
  // operator switches advisor before this resolves (Codex Review P1).
  onResendSuccess: (entryId: number, advisorId: number) => Promise<unknown>;
  // Entries whose resend mail went out but whose durable record rolled back (commit-phase
  // failure), mapped to the resend-time mail header snapshot. Terminally latched in the panel
  // so the detail shows a "delivered but not recorded — reload" warning (rendering the snapshot,
  // not the live row) and never re-enables the button (Codex Review P1).
  resendUnrecordedSnapshots: ReadonlyMap<number, ResendMailSnapshot>;
  // Marks the entry as delivered-but-unrecorded in the panel (commit-phase failure), carrying
  // the resend-time mail header snapshot.
  onResendUnrecorded: (entryId: number, snapshot: ResendMailSnapshot) => void;
}

function SystemAccessLogTable({
  entries,
  onCancel,
  onEdit,
  editPendingIds,
  resentPendingIds,
  onResendSuccess,
  resendUnrecordedSnapshots,
  onResendUnrecorded,
}: Props) {
  const theme = useTheme();
  const [openId, setOpenId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  React.useEffect(() => {
    setPage(0);
  }, [entries.length]);

  const pagedEntries = rowsPerPage > 0 ? entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : entries;

  const toggleOpen = (id: number) => {
    setOpenId((current) => (current === id ? null : id));
  };

  if (entries.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography sx={{ color: theme.palette.text.disabled, fontSize: 13 }}>
          Keine Einträge zu den gewählten Filtern.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small" aria-label="Aktionsprotokoll">
          <TableHead>
            <TableRow>
              {['DATUM', 'SYSTEM', 'AKTION', 'STATUS', 'AUSGELÖST VON', '__actions'].map((header, idx) => (
                <TableCell
                  key={header || `col-${idx}`}
                  align={idx === 5 ? 'right' : 'left'}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    color: theme.palette.text.disabled,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    width: idx === 5 ? 56 : undefined,
                  }}
                >
                  {header.startsWith('__') ? '' : header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedEntries.map((entry) => {
              const isOpen = openId === entry.id;
              const actionCfg = ACTION_CONFIG[entry.action_type];
              const changedCount = entry.history.filter((h) => h.entry_type === 'updated').length;
              const sentCount = entry.history.filter(
                (h) => h.entry_type === 'sent' || h.entry_type === 'resent',
              ).length;

              return (
                <React.Fragment key={entry.id}>
                  <TableRow
                    data-testid={`log-row-${entry.id}`}
                    data-process-status={entry.process_status || 'none'}
                    onClick={() => toggleOpen(entry.id)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.grey[500], 0.04),
                      },
                      '& td': {
                        borderBottom: isOpen ? 'none' : `1px solid ${theme.palette.divider}`,
                      },
                    }}
                  >
                    <TableCell sx={{ fontSize: 13, color: theme.palette.text.secondary }}>
                      {formatRowDate(entry)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{entry.provider_name || '—'}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: actionCfg?.color || theme.palette.text.primary,
                        }}
                      >
                        {actionCfg?.label || entry.action_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <SystemAccessLogStatusPill
                        processStatus={entry.process_status}
                        scheduledAt={entry.scheduled_at}
                        isManualEdit={Boolean(entry.is_manual_edit)}
                        changedCount={changedCount}
                        sentCount={sentCount}
                        errorReason={entry.error_reason}
                        errorDetail={entry.error_detail}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: theme.palette.text.secondary }}>
                      {entry.triggered_by_name || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={isOpen ? 'Details schließen' : 'Details öffnen'}
                        aria-expanded={isOpen}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOpen(entry.id);
                        }}
                      >
                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{
                        p: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Collapse in={isOpen} timeout={180} unmountOnExit>
                        <SystemAccessLogDetail
                          entry={entry}
                          onCancel={onCancel}
                          onEdit={onEdit}
                          editPending={editPendingIds.has(entry.id)}
                          resendLatched={resentPendingIds.has(entry.id)}
                          onResendSuccess={onResendSuccess}
                          resendUnrecordedSnapshot={resendUnrecordedSnapshots.get(entry.id) ?? null}
                          onResendUnrecorded={onResendUnrecorded}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={entries.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage="Zeilen pro Seite"
        labelDisplayedRows={({ from, to, count }) =>
          rowsPerPage === -1 ? `${count} Einträge` : `${from}–${to} von ${count}`
        }
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      />
    </>
  );
}

export default SystemAccessLogTable;
